import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { 
  Users, 
  Target, 
  Code, 
  TrendingUp, 
  PenTool, 
  Shield, 
  DollarSign,
  ChevronRight,
  Sparkles,
  Award,
  Briefcase,
  Globe,
  Mail,
  Linkedin,
  Twitter,
  ExternalLink,
  Star,
  CheckCircle,
  Heart,
  Building,
  Zap,
  Megaphone,
  Scale,
  UserCheck
} from 'lucide-react'

import { teamMembers } from '../../data/teamMembers'

const Slide5Team = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: false
  })

  const [hoveredMember, setHoveredMember] = useState(null)
  const [activeCategory, setActiveCategory] = useState('all')
  const [isMobile, setIsMobile] = useState(false)
  const [expandedMembers, setExpandedMembers] = useState(new Set())

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const toggleMemberExpansion = (memberId) => {
    setExpandedMembers(prev => {
      const newSet = new Set(prev)
      if (newSet.has(memberId)) {
        newSet.delete(memberId)
      } else {
        newSet.add(memberId)
      }
      return newSet
    })
  }

  const isMemberExpanded = (memberId) => {
    return expandedMembers.has(memberId)
  }

  const categories = [
    { 
      id: 'all', 
      name: 'All Team', 
      icon: Users, 
      count: teamMembers.length,
      color: 'cyan'
    },
    { 
      id: 'leadership', 
      name: 'Leadership', 
      icon: Target, 
      count: teamMembers.filter(m => m.category === 'leadership').length,
      color: 'blue'
    },
    { 
      id: 'strategy', 
      name: 'Strategy', 
      icon: TrendingUp, 
      count: teamMembers.filter(m => m.category === 'strategy' || m.category === 'growth' || m.category === 'outreach').length,
      color: 'purple'
    },
    { 
      id: 'engineering', 
      name: 'Engineering', 
      icon: Code, 
      count: teamMembers.filter(m => m.category === 'engineering' || m.category === 'architecture').length,
      color: 'green'
    },
    { 
      id: 'design', 
      name: 'Design', 
      icon: PenTool, 
      count: teamMembers.filter(m => m.category === 'design').length,
      color: 'pink'
    },
    { 
      id: 'finance', 
      name: 'Finance & Legal', 
      icon: Shield, 
      count: teamMembers.filter(m => m.category === 'finance' || m.category === 'legal').length,
      color: 'yellow'
    }
  ]

  const filteredMembers = activeCategory === 'all' 
    ? teamMembers 
    : teamMembers.filter(member => 
        activeCategory === 'leadership' ? member.category === 'leadership' :
        activeCategory === 'strategy' ? ['strategy', 'growth', 'outreach'].includes(member.category) :
        activeCategory === 'engineering' ? ['engineering', 'architecture'].includes(member.category) :
        member.category === activeCategory
      )

  const colorVariants = {
    cyan: { text: 'text-cyan-400', bg: 'bg-cyan-400/10', border: 'border-cyan-400/30', button: 'bg-gradient-to-r from-cyan-500 to-cyan-600' },
    blue: { text: 'text-blue-400', bg: 'bg-blue-400/10', border: 'border-blue-400/30', button: 'bg-gradient-to-r from-blue-500 to-blue-600' },
    purple: { text: 'text-purple-400', bg: 'bg-purple-400/10', border: 'border-purple-400/30', button: 'bg-gradient-to-r from-purple-500 to-purple-600' },
    green: { text: 'text-green-400', bg: 'bg-green-400/10', border: 'border-green-400/30', button: 'bg-gradient-to-r from-green-500 to-green-600' },
    pink: { text: 'text-pink-400', bg: 'bg-pink-400/10', border: 'border-pink-400/30', button: 'bg-gradient-to-r from-pink-500 to-pink-600' },
    yellow: { text: 'text-yellow-400', bg: 'bg-yellow-400/10', border: 'border-yellow-400/30', button: 'bg-gradient-to-r from-yellow-500 to-yellow-600' }
  }

  const getCategoryIcon = (category) => {
    switch(category) {
      case 'leadership': return Target
      case 'strategy': return TrendingUp
      case 'growth': return Zap
      case 'outreach': return Megaphone
      case 'engineering': return Code
      case 'architecture': return Building
      case 'design': return PenTool
      case 'finance': case 'legal': return Scale
      default: return UserCheck
    }
  }

  const activeColors = colorVariants[categories.find(c => c.id === activeCategory)?.color || 'cyan']

  return (
    <div 
      id="team" 
      ref={ref}
      className="relative min-h-screen w-full bg-black overflow-hidden py-8 md:py-12"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900/20 to-black"></div>
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/20 to-transparent"></div>
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(90deg, #22d3ee 1px, transparent 1px),
                             linear-gradient(180deg, #22d3ee 1px, transparent 1px)`,
            backgroundSize: '50px 50px',
          }}></div>
        </div>
      </div>

      <div className="relative z-10 container mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-8 md:mb-12 px-2">
          <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20">
            <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full"></div>
            <span className="text-cyan-300 text-xs md:text-sm font-medium">MEET THE TEAM</span>
          </div>
          
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-4">
            <span className="block text-white mb-1 md:mb-2">The Minds Behind</span>
            <span className="block bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
              Quavrion
            </span>
          </h2>
          
          <p className="text-sm sm:text-base md:text-lg text-gray-400 max-w-2xl mx-auto">
            Visionaries, architects, and strategists working together to build the future
          </p>
        </div>

        {/* Category Filters */}
        <div className="mb-6 md:mb-8 overflow-x-auto pb-2">
          <div className="flex gap-2 md:gap-3 min-w-max px-2">
            {categories.map((category) => {
              const Icon = category.icon
              const isActive = activeCategory === category.id
              const colors = colorVariants[category.color]
              return (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`flex items-center gap-2 px-3 md:px-4 py-2 md:py-2.5 rounded-lg transition-all duration-300 flex-shrink-0 ${
                    isActive
                      ? `${colors.button} text-white shadow-lg`
                      : 'bg-white/5 text-gray-300 hover:bg-white/10'
                  }`}
                >
                  <Icon className="w-4 h-4 md:w-5 md:h-5" />
                  <span className="text-sm font-medium whitespace-nowrap">{category.name}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                    isActive ? 'bg-white/20' : 'bg-white/5'
                  }`}>
                    {category.count}
                  </span>
                </button>
              )
            })}
          </div>
        </div>

        {/* Team Stats */}
        <div className="mb-6 md:mb-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {[
              { label: 'Team Members', value: teamMembers.length, icon: Users, color: 'text-cyan-400' },
              { label: 'Leadership Team', value: teamMembers.filter(m => m.category === 'leadership').length, icon: Target, color: 'text-blue-400' },
              { label: 'Departments', value: new Set(teamMembers.map(m => m.category)).size, icon: Building, color: 'text-purple-400' },
              { label: 'Expertise', value: '10+', icon: Award, color: 'text-green-400' },
            ].map((stat, index) => {
              const Icon = stat.icon
              return (
                <div
                  key={index}
                  className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-lg p-3 md:p-4 text-center"
                >
                  <div className="flex justify-center mb-2">
                    <Icon className={`w-4 h-4 md:w-5 md:h-5 ${stat.color}`} />
                  </div>
                  <div className="text-lg md:text-xl font-bold text-white mb-1">
                    {stat.value}
                  </div>
                  <div className="text-xs text-gray-400">
                    {stat.label}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Team Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6 mb-8 md:mb-12">
          <AnimatePresence>
            {filteredMembers.map((member, index) => {
              const CategoryIcon = getCategoryIcon(member.category)
              const colors = colorVariants[
                member.category === 'leadership' ? 'blue' :
                ['strategy', 'growth', 'outreach'].includes(member.category) ? 'purple' :
                ['engineering', 'architecture'].includes(member.category) ? 'green' :
                member.category === 'design' ? 'pink' :
                ['finance', 'legal'].includes(member.category) ? 'yellow' : 'cyan'
              ]
              const isExpanded = isMemberExpanded(member.id)
              const showFullQuote = (isMobile && isExpanded) || (!isMobile && hoveredMember === member.id)
              
              return (
                <motion.div
                  key={member.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{ delay: index * 0.1, duration: 0.4 }}
                  onMouseEnter={() => !isMobile && setHoveredMember(member.id)}
                  onMouseLeave={() => !isMobile && setHoveredMember(null)}
                  className="group"
                >
                  <div className={`h-full bg-gradient-to-br from-gray-900/80 to-black/80 rounded-xl md:rounded-2xl border ${colors.border} p-4 md:p-5 transition-all duration-300 ${
                    hoveredMember === member.id && !isMobile ? 'transform md:scale-105' : ''
                  }`}>
                    
                    {/* Profile Header */}
                    <div className="flex items-start gap-3 md:gap-4 mb-4">
                      {/* Avatar */}
                      <div className="relative flex-shrink-0">
                        <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl bg-gradient-to-br from-white/10 to-transparent border border-cyan-400/30 flex items-center justify-center">
                          <span className="text-xl md:text-2xl font-bold text-cyan-400">
                            {member.name.charAt(0)}
                          </span>
                        </div>
                        <div className="absolute -top-1 -right-1">
                          <div className={`p-1 rounded-lg ${colors.bg}`}>
                            <CategoryIcon className={`w-3 h-3 ${colors.text}`} />
                          </div>
                        </div>
                      </div>

                      {/* Name & Title */}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base md:text-lg font-bold text-white mb-1 truncate">
                          {member.name}
                        </h3>
                        <div className="text-sm text-gray-300 mb-1">
                          {member.title}
                        </div>
                        <div className={`text-xs font-medium ${colors.text}`}>
                          {member.tagline}
                        </div>
                      </div>
                    </div>

                    {/* Quote */}
                    <div className="mb-4">
                      <div className="flex items-start gap-2">
                        <QuoteIcon className="w-3 h-3 md:w-4 md:h-4 text-cyan-400/50 mt-1 flex-shrink-0" />
                        <p className="text-sm text-gray-300 leading-relaxed">
                          {showFullQuote ? member.quote : `${member.quote.split(' ').slice(0, 30).join(' ')}...`}
                        </p>
                      </div>
                    </div>

                    {/* Full Quote on Hover/Click - Animation */}
                    {!isMobile && hoveredMember === member.id && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-4 pt-4 border-t border-gray-800"
                      >
                        <p className="text-sm text-gray-300 leading-relaxed">
                          {member.quote}
                        </p>
                      </motion.div>
                    )}

                    {/* Category Badge */}
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-800">
                      <div className={`text-xs px-2 py-1 rounded-full ${colors.bg} ${colors.text}`}>
                        {member.category.toUpperCase()}
                      </div>
                      <button 
                        onClick={() => {
                          if (isMobile) {
                            toggleMemberExpansion(member.id)
                          } else {
                            setHoveredMember(hoveredMember === member.id ? null : member.id)
                          }
                        }}
                        className={`text-xs px-3 py-1 rounded-lg transition-colors ${
                          showFullQuote 
                            ? 'bg-cyan-400/10 text-cyan-400' 
                            : 'bg-white/5 text-gray-400 hover:bg-white/10'
                        }`}
                      >
                        {showFullQuote ? 'Show Less' : 'Read More'}
                      </button>
                    </div>
                  </div>
                </motion.div>
              )
            })}
          </AnimatePresence>
        </div>

        {/* Join Our Team CTA */}
        <div className={`rounded-xl md:rounded-2xl border ${activeColors.border} bg-gradient-to-br from-gray-900/80 to-black/80 p-6 md:p-8`}>
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-8">
            <div className="text-center md:text-left">
              <h3 className="text-xl md:text-2xl font-bold text-white mb-2">
                Join Our Mission
              </h3>
              <p className="text-sm md:text-base text-gray-300 max-w-xl">
                We're building the future of technology. If you're passionate about innovation and excellence, we'd love to hear from you.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <button className={`${activeColors.button} text-white px-6 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2`}>
                <span>View Careers</span>
                <ChevronRight className="w-4 h-4" />
              </button>
              <button className="px-6 py-3 bg-white/5 border border-gray-700 text-gray-300 rounded-lg font-medium hover:bg-white/10 transition-colors">
                Contact Us
              </button>
            </div>
          </div>
        </div>

        {/* Team Values */}
        <div className="mt-8 md:mt-12">
          <h3 className="text-xl md:text-2xl font-bold text-white mb-4 md:mb-6 text-center">
            Our Values
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {[
              { 
                title: "Innovation First", 
                description: "We challenge the status quo and embrace bold ideas",
                icon: Sparkles,
                color: "cyan"
              },
              { 
                title: "Excellence Always", 
                description: "Quality is non-negotiable in everything we do",
                icon: Star,
                color: "blue"
              },
              { 
                title: "Collaborative Spirit", 
                description: "Great things happen when brilliant minds work together",
                icon: Users,
                color: "purple"
              },
            ].map((item, index) => {
              const Icon = item.icon
              const colors = colorVariants[item.color]
              return (
                <div
                  key={index}
                  className={`p-4 md:p-6 rounded-xl md:rounded-2xl border ${colors.border} bg-gradient-to-br from-gray-900/50 to-black/50`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`p-2 rounded-lg ${colors.bg}`}>
                      <Icon className={`w-5 h-5 md:w-6 md:h-6 ${colors.text}`} />
                    </div>
                    <h4 className="text-lg font-bold text-white">
                      {item.title}
                    </h4>
                  </div>
                  <p className="text-sm md:text-base text-gray-300">
                    {item.description}
                  </p>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

// Custom Quote Icon component
const QuoteIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
  </svg>
)

export default Slide5Team